import { put, select } from 'redux-saga/effects';
import gql from 'graphql-tag';
import {
  GTM,
  Events as AnalyticsEvents,
  Events,
  Categories,
  AnalyticsAttributes,
  track as trackAction,
  transaction,
} from '@app/analytics';
import { isEmpty } from 'lodash';
import ironLegacyClient from '@app/data/ironLegacyClient';
import { apolloClient } from '@app/data/apollo';
import type { PortfolioItem, LegacyProduct } from '@app/types';
import { getCustomerId, getCustomerMarket } from '../customer/selectors';

const IS_NEW_SELLER_QUERY = gql`
  query IsNewSeller {
    viewer {
      asksPending: asks(state: PENDING) {
        pageInfo {
          totalCount
        }
      }
      asksHistory: asks(state: HISTORICAL) {
        pageInfo {
          totalCount
        }
      }
    }
  }
`;

export function* track(
  portfolioItem: PortfolioItem,
  action,
  item = {},
  initialProduct: LegacyProduct,
  paymentMethod?: string,
  isGlobalNotificationEmailEnabled?: boolean,
) {
  // Warning
  // Do not change these conditions before speaking with Stokes + Ian.
  // We have a lot of abnormal states that are don't match up exactly how we like
  const bidAction = action === 'bid';
  const askAction = action === 'ask';
  const BID = bidAction && !portfolioItem.matchedWith;
  const BUY_NOW = bidAction && !!portfolioItem.matchedWith;
  const ASK = askAction && !portfolioItem.matchedWith;
  const SELL_NOW = askAction && !!portfolioItem.matchedWith;

  // Review this, fixes incident-92.
  let product;
  if (initialProduct) {
    product = initialProduct;
  } else {
    // eslint-disable-next-line prefer-destructuring
    product = portfolioItem.product;
  }

  const { Customer: customer } = portfolioItem;

  const commonGAEventData = {
    category: product.productCategory,
    label: product.title,
    value: portfolioItem.amount,
    decorators: {
      customer,
      product,
      portfolioItem,
    },
    properties: {},
  };

  if (paymentMethod) {
    commonGAEventData.properties[AnalyticsAttributes.cdPaymentMethod] =
      paymentMethod;
  }

  switch (true) {
    case BID:
      if (!isEmpty(item)) {
        yield put(
          trackAction({
            ...commonGAEventData,
            event: Events.UpdatedBid,
            category: Categories.AccountBuying,
            label: product.id,
          }),
        );
      } else {
        yield put(
          trackAction({
            ...commonGAEventData,
            event: Events.BidSuccess,
          }),
        );
      }

      if (!portfolioItem.chainId) {
        yield put(
          trackAction({
            ...commonGAEventData,
            event: AnalyticsEvents.BidNewComplete,
          }),
        );
      }

      GTM.trackTransaction('bid', {
        ...portfolioItem,
        isGlobalNotificationEmailEnabled,
      });
      break;
    case BUY_NOW:
      {
        const customerId = yield select((state) => getCustomerId(state));
        const marketName = yield select((state) => getCustomerMarket(state));
        const portfolioItemResponses = yield Promise.allSettled([
          ironLegacyClient.getCustomerPortfolioItems({
            customerId,
            type: 'buying',
            status: 'pending',
            limit: 1,
            marketName,
          }),
          ironLegacyClient.getCustomerPortfolioItems({
            customerId,
            type: 'buying',
            status: 'history',
            limit: 1,
            marketName,
          }),
        ]);

        const isNewBuyer =
          portfolioItemResponses[0]?.value?.body?.Pagination?.total === 1 &&
          portfolioItemResponses[1]?.value?.body?.Pagination?.total === 0;

        // GTM.trackTransaction must be called before calls to Google Analytics in order
        // for data layer variables to be used when tracking Google Analytic events
        GTM.trackTransaction('buy', {
          ...portfolioItem,
          isGlobalNotificationEmailEnabled,
        });

        yield put(
          trackAction({
            ...commonGAEventData,
            event: AnalyticsEvents.BuyerBought,
          }),
        );

        yield put(
          trackAction({
            ...commonGAEventData,
            event: isNewBuyer
              ? AnalyticsEvents.NewBuyerBought
              : AnalyticsEvents.RepeatBuyerBought,
          }),
        );
      }

      yield put(
        transaction({
          portfolioItem,
          customer,
          customerType: 'Buyer',
          paymentMethod,
        }),
      );

      break;
    case ASK:
      if (!isEmpty(item)) {
        yield put(
          trackAction({
            ...commonGAEventData,
            event: Events.UpdatedAsk,
            category: Categories.AccountSelling,
            label: product.id,
          }),
        );
      } else {
        yield put(
          trackAction({
            ...commonGAEventData,
            event: AnalyticsEvents.AskComplete,
          }),
        );
      }

      GTM.trackTransaction('ask', portfolioItem);
      break;
    case SELL_NOW: {
      const { data } = yield apolloClient().query({
        query: IS_NEW_SELLER_QUERY,
        variables: {},
      });

      const isNewSeller =
        data?.viewer?.asksHistory?.pageInfo?.totalCount === 0 &&
        data?.viewer?.asksPending?.pageInfo?.totalCount <= 1;

      GTM.trackTransaction('sell', portfolioItem);
      yield put(
        trackAction({
          ...commonGAEventData,
          event: AnalyticsEvents.AskComplete,
        }),
      );

      yield put(
        trackAction({
          ...commonGAEventData,
          event: AnalyticsEvents.SellerSold,
        }),
      );

      yield put(
        trackAction({
          ...commonGAEventData,
          event: isNewSeller
            ? AnalyticsEvents.NewSellerSold
            : AnalyticsEvents.RepeatSellerSold,
        }),
      );

      yield put(
        transaction({
          portfolioItem,
          customer,
          customerType: 'Seller',
          paymentMethod,
        }),
      );
      break;
    }

    default:
      break;
  }
}

export default track;
